import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';

import useFetchSites from '../hooks/useFetchSites';
import useFetchDeclarations from '../hooks/useFetchDeclarations';
import useFetchDoxExt from '../hooks/useFetchDoxExt';
import useFetchIntervenants from '../hooks/useFetchIntervenants';
import useFetchLiensExternes from '../hooks/useFetchLiensExternes';

import useSectionVisibility from '../hooks/useSectionVisibility';

import Loader from '../components/Loader';
import MapPopup from '../components/MapPopup';
import Map from '../components/Dashboard/Map';
import ListDoxExt from '../components/ListDoxExt';
import ListDeclaration from '../components/ListDeclaration';
import ListIntervenant from '../components/ListIntervenant';

import ExternalLinksCard from '../components/ExternalLinksCard';
import SectionVisibilityPanel from '../components/SectionVisibilityPanel.jsx';

import { sanitizeText } from '../utils/SanitizeText.js'
import { formatDate, formatCurrency, fmtBool } from '../utils/Format';

import SectionCard from '../utils/SectionCard.jsx';
import InfoItem from '../utils/InfoItem.jsx';

import placeholderSitePhoto from "../assets/img/placeholder.png";

const SiteDetail = () => {
	const { uuid } = useParams();

	const { site, fetchSiteByUUID, loading: loadingSite } = useFetchSites();
	const { siteDeclarations, fetchDeclarationsBySiteUUID } = useFetchDeclarations();
	const { doxExtByUUID: siteDoxExt, fetchDoxExtByUUID } = useFetchDoxExt();
	const { relIntervenants, fetchRelationsIntervenantsBySiteUUID } = useFetchIntervenants();
	const { liensExts, fetchLiensExtsByUUIDAndOrigine, saveLiensExternesDiff, loading: loadingLiens, mutating } = useFetchLiensExternes();

	const [activeTab, setActiveTab] = useState('coord');
	const [mapOpen, setMapOpen] = useState(false);

	const [previewOpen, setPreviewOpen] = useState(false);

	const [showMap, setShowMap] = useState(false);
	const [mapSignal, setMapSignal] = useState(0);

	const [openSettings, setOpenSettings] = useState(false);
	const settingsBtnRef = useRef(null);
	const settingsMenuRef = useRef(null);

	const toggleMapBack = (next) => {
		setShowMap(next);
		setMapSignal((s) => s + 1);
	};

	const hasCoords = site?.Localisation?.latitude && site?.Localisation?.longitude;

	const [imgOpen, setImgOpen] = useState(false);

	const raw = String(site?.b64_ImageSite || "").trim();
	const hasRealImage = !!raw;
	const imageSrc = hasRealImage
		? (raw.startsWith("data:image") ? raw : `data:image/jpeg;base64,${raw}`)
		: placeholderSitePhoto;

	const PjTemplate = (props) => props.PJ ? (
		<i className="text-blue-500 fa-paperclip fas"></i>
	) : null;

	const ArchiverTemplate = (props) => props.Archiver_Enr ? (
		<i className="text-blue-500 fa-square-check fas"></i>
	) : (
		<i className="w-15 h-15 text-blue-500 fa-square far"></i>
	);

	const statusTemplate = (props) => {
		const normalized = props.Etat?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
		const colors = {
			'ouvert': 'bg-green-500',
			'cloture': 'bg-red-500',
			'a suivre': 'bg-yellow-500',
			'pre-rapport': 'bg-blue-500',
			'devis valide': 'bg-purple-500',
			'sans suite': 'bg-red-700',
		};
		let color = 'bg-gray-400';
		for (const [k, v] of Object.entries(colors)) if (normalized.startsWith(k)) { color = v; break; }
		return <span className={`px-2 py-1 rounded text-white ${color}`}>{props.Etat}</span>;
	};

	useEffect(() => {
		if (!imgOpen) return;
		const onKey = (e) => e.key === "Escape" && setImgOpen(false);
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [imgOpen]);

	useEffect(() => {
		if (!openSettings) return;

		const handleClickOutside = (e) => {
			const btn = settingsBtnRef.current;
			const menu = settingsMenuRef.current;

			if (btn?.contains(e.target)) return;
			if (menu?.contains(e.target)) return;

			setOpenSettings(false);
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("touchstart", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("touchstart", handleClickOutside);
		};
	}, [openSettings]);

	const sections = [
		{ key: 'refs', label: "Références opérationnelles" },
		{ key: 'dates', label: "Dates clés" },
		{ key: 'archive', label: "Archivage & Partage" },
		{ key: 'parties', label: "Parties prenantes" },
		{ key: 'financier', label: "Financier" },
		{ key: 'do', label: "Assurance D.O." },
		{ key: 'divers', label: "Divers / Réserves" },
		{ key: 'remarques', label: "Remarques" },
		{ key: 'liens', label: "Liens externes" },
	];

	const storageKey = `site_sections_${uuid}`;

	const vis = useSectionVisibility(storageKey, sections);

	useEffect(() => {
		fetchSiteByUUID(uuid);
		fetchDeclarationsBySiteUUID(uuid);
		fetchDoxExtByUUID(uuid);
		fetchRelationsIntervenantsBySiteUUID(uuid);

		fetchLiensExtsByUUIDAndOrigine(uuid, "Site");
	}, [uuid]);

	const refreshDoxExt = () => {
		fetchDoxExtByUUID(uuid);
	};

	// if (!site || !siteDeclarations || !siteDoxExt) {
	// 	return <div className="flex flex-1 justify-cen]er items-center"><Loader /></div>;
	// }

	const columnsIntervenant = [
		{ field: "Lot", headerText: "Lot Désigné", width: "250px" },
		{ field: "Nom", headerText: "Société", width: "300px" },
		{ field: "_Type", headerText: "Type", width: "180px", },
		{ field: "ActiviteSite", headerText: "Activité", width: "250px" },
		{ field: "Date_Contrat", headerText: "Date de Contrat", width: "250px" },
		{ field: "Nom_Contact", headerText: "Nom du Contact", width: "250px" },
		{ field: "Adresse", headerText: "Adresse", width: "230px" },
		{ field: "Ville", headerText: "Ville", width: "180px" },
		{ field: "CP", headerText: "CP", width: "100px" },
		{ field: "Mail", headerText: "Em@il", width: "230px" },
		{ field: "Tel", headerText: "Telephone", width: "180px" },
		{ field: "Forme_juridique", headerText: "Forme Juridique", width: "200px" },
		{ field: "Siret", headerText: "N° Siret", width: "180px" },
		{ field: "_Date_Crea", headerText: "Créé le", type: "date", format: "dd/MM/yyyy", width: "130px" },
	];

	const columnsDeclarations = [
		{ field: "Declaration_PJ", headerText: "PJ", width: "85px", textAlign: "Center", template: PjTemplate },
		{ field: "Archiver_Enr", headerText: "Archivé", width: "90px", textAlign: "Center", template: ArchiverTemplate },
		{ field: "Code_Procedure", headerText: "Code", width: "150px" },
		{ field: "Etat", headerText: "Etat", width: "130px", template: statusTemplate },
		{ field: "Charge_affaire", headerText: "CA", width: "180px" },
		{ field: "Date_creation", headerText: "Réclamation", width: "200px", type: "date", format: "dd/MM/yyyy" },
		{ field: "_Date_Crea", headerText: "Crée", width: "130px", type: "date", format: "dd/MM/yyyy" },
		{ field: "Montant_Du_Definitif", headerText: "Provision PP", width: "200px" },
		{ field: "Cout_tt_Pathologie", headerText: "Coût Pathologies", width: "180px", textAlign: "Right" },
		{ field: "Montant_Du_Definitif", headerText: "Coût Déclaration", width: "180px", textAlign: "Right" },
		{ field: "Libelle", headerText: "Libellé", width: "400px", cssClass: "font-bold" },
		{ field: "Expert_Ref", headerText: "Ref Expert", width: "200px" },
		{ field: "Expert_Societe", headerText: "Expert", width: "200px" },
		{ field: "Assureur_Client_Ref", headerText: "Ref Assureur", width: "200px" },
		{ field: "Assureur_Client_Nom", headerText: "Assureur", width: "200px" },
		{ field: "Courtier_Ref", headerText: "Libellé", width: "200px" },
		{ field: "Assureur_Interne_Ref", headerText: "Ref Assureur Interne", width: "200px", },
		{ field: "GPA_OS", headerText: "GPA OS", width: "200px", },
		{ field: "GPA_Num_Logement", headerText: "Bât - Loc", width: "200px", },
		{ field: "GPA_Intervenant", headerText: "GPA Intervenant", width: "200px" },
		{ field: "GPA_Locataire", headerText: "GPA Locataire", width: "200px" },
	];

	// console.log("relIntervenants", relIntervenants);

	//console.log(placeholderSitePhoto);


	return (
		<div class="flex-1 p-4 lg:p-8">
			<div class="gap-6 grid grid-cols-12">
				<div class="gap-6 grid grid-cols-12 col-span-12 xxl:col-span-9">
					<div class="col-span-12">


						<div className="mx-auto w-full">
							<div className="mb-4 w-full rounded-xl bg-slate-900 px-5 py-3 shadow-md shadow-slate-900/40 border border-slate-700 sm:px-7 sm:py-4">
								{/* Breadcrumb */}
								<nav
									id="url"
									className="flex items-center gap-2 text-xs font-semibold text-slate-300 sm:text-sm mb-2"
								>
									<Link
										to="/sites"
										className="hover:text-white decoration-orange-500 hover:underline hover:underline-offset-2 transition hover:translate-y-[-1px] duration-200"
									>
										Sites
									</Link>
									<span className="cursor-default text-slate-500">/</span>
									<span className="inline-flex items-center text-orange-400">
										{loadingSite ? (
											<span className="inline-block bg-slate-700/70 rounded-full w-28 h-3.5 align-middle animate-pulse" />
										) : (
											<span className="truncate max-w-[12rem] sm:max-w-xs">{site?.Nom}</span>
										)}
									</span>
								</nav>

								<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
									<p className="flex flex-wrap items-center gap-3 font-semibold text-white text-base sm:text-lg">
										<span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-tr from-orange-500 via-orange-400 to-amber-300 shadow-sm shadow-orange-500/40">
											<i className="fa-solid fa-building fa-sm" />
										</span>

										{loadingSite ? (
											<span className="inline-block bg-slate-700/70 rounded-full w-[10rem] h-4 animate-pulse" />
										) : (
											<>
												<span className="truncate max-w-xs sm:max-w-md">{site?.Nom}</span>

												{site?.Adresse1 && site?.Ville && site?.CP && (
													<button
														type="button"
														onClick={() => setMapOpen(true)}
														className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-orange-300 hover:text-white hover:underline underline-offset-2 transition"
													>
														<i className="fa-solid fa-location-dot" />
														<span className="truncate max-w-xs sm:max-w-md">
															{site?.Adresse1}
															<span className="font-semibold">
																{' '}
																({site?.Ville}{site?.CP ? `, ${site?.CP}` : ''})
															</span>
														</span>
													</button>
												)}
											</>
										)}
									</p>

									<div className="flex items-center gap-2">
										<button
											type="button"
											title="Aperçu / Voir"
											aria-label="Aperçu"
											disabled={loadingSite}
											onClick={() => setPreviewOpen(v => !v)}
											className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-gray-100 hover:bg-white/20 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-white/60 transition"
										>
											<i className="fa-solid fa-eye-low-vision" />
										</button>

										<div className="relative">
											<button
												ref={settingsBtnRef}
												type="button"
												title="Paramètres"
												aria-label="Paramètres"
												disabled={loadingSite}
												onClick={() => setOpenSettings(v => !v)}
												className="group inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-gray-100 hover:bg-white/20 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-white/60 transition"
											>
												<i className="fa-solid fa-gear group-hover:rotate-[20deg] transition-transform duration-200" />
											</button>

											{openSettings && (
												<div
													ref={settingsMenuRef}
													className="absolute right-0 mt-2 w-52 rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 z-50"
												>
													<Link
														to={`/sites/edit/${uuid}`}
														state={{ site }}
														onClick={() => setOpenSettings(false)}
														className="flex w-full items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100"
													>
														<i className="fa-solid fa-pen" />
														<span>Modifier le site</span>
													</Link>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>



							<SectionVisibilityPanel
								open={previewOpen}
								onClose={() => setPreviewOpen(false)}
								sections={sections}
								storageKey={storageKey}
								floating
							/>
							<div className="mb-1 border-gray-200 border-b">
								<ul className="flex flex-wrap -mb-px" role="tablist">
									{[
										{ id: 'coord', label: 'Coordonnées' },
										{ id: 'declarations', label: 'Déclarations' },
										{ id: 'lstIntervenants', label: 'Liste Intervenants / GPA' },
										{ id: 'docs', label: 'Documents Externes' },
									].map((tab) => (
										<li key={tab.id} className="mr-2" role="presentation">
											<button
												className={`text-base font-semibold text-center py-4 px-4 rounded-t-lg border-b-5 ${activeTab === tab.id
													? 'text-orange-600 border-8 border-orange-300'
													: 'text-orange-400 border-transparent hover:text-orange-500'
													} transition duration-300 ease-in-out`}
												onClick={() => setActiveTab(tab.id)}
												type="button"
												role="tab"
												aria-controls={tab.id}
												aria-selected={activeTab === tab.id}
											>
												{tab.label}
											</button>
										</li>
									))}
								</ul>
							</div>

							<MapPopup
								isVisible={mapOpen}
								onClose={() => setMapOpen(false)}
								location={site}
								title={`Localisation du Site '${site?.Nom}'`}
							/>

							<div id="myTabContent">
								{activeTab === 'coord' && (
									<div id="form-info" class="space-y-4 mx-auto">

										<div className="gap-3 grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 grid-flow-row-dense">
											{/* 1) Identité */}
											<div className="col-span-1 2xl:col-span-2">
												<SectionCard loading={loadingSite} title="Identité du site" icon="fa-id-card">
													<div className="gap-4 grid grid-cols-[96px_1fr] sm:grid-cols-[120px_1fr]">
														<div className="flex">
															<button
																type="button"
																disabled={!hasRealImage}
																onClick={() => setImgOpen(true)}
																className={`relative block w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 ${hasRealImage ? "cursor-zoom-in" : "cursor-default opacity-95"
																	}`}
																title={hasRealImage ? "Agrandir l’image" : "Aucune image disponible"}
															>
																<img
																	src={imageSrc}
																	alt="Photo du site"
																	className="absolute inset-0 w-full h-full object-contain"
																	loading="lazy"
																	onError={(e) => {
																		e.currentTarget.onerror = null;
																		e.currentTarget.src = placeholderSitePhoto;
																	}}
																/>
																{hasRealImage && (
																	<span className="absolute inset-0 ring-0 group-hover:ring-2 group-hover:ring-orange-400/60 transition duration-200 pointer-events-none" />
																)}
															</button>
														</div>
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Nom du site" value={site?.Nom} />
															<InfoItem label="Société" value={site?.Societe} />
															<InfoItem label="Chargé d’affaires" value={site?.CA} />
															<InfoItem label="Entité / Région" value={site?.Entite} />
															<InfoItem label="Centre de profit" value={site?.Centre_Profit} />
															<InfoItem label="Type d’ouvrage" value={site?.Type_Ouvrage} />
														</div>
													</div>
													{imgOpen && hasRealImage && (
														<div
															role="dialog"
															aria-modal="true"
															className="z-[1000] fixed inset-0 flex justify-center items-center bg-black/50 p-4"
															onClick={() => setImgOpen(false)}
														>
															<div
																className="relative max-w-[90vw] max-h-[90vh]"
																onClick={(e) => e.stopPropagation()}
															>
																<img
																	src={imageSrc}
																	alt="Aperçu"
																	className="shadow-2xl rounded-xl max-w-[90vw] max-h-[90vh] object-contain"
																/>
																<button
																	type="button"
																	aria-label="Fermer"
																	onClick={() => setImgOpen(false)}
																	className="inline-flex -top-3 -right-3 absolute justify-center items-center bg-orange-600 hover:bg-orange-500 shadow-lg rounded-full w-9 h-9 text-white transition-all duration-300"
																>
																	<i className="fa-solid fa-xmark" />
																</button>
															</div>
														</div>
													)}
												</SectionCard>
											</div>

											{/* 2) Adresse & Localisation */}
											<div className="lg:col-span-2 2xl:col-span-2">
												{hasCoords ? (
													<SectionCard
														loading={loadingSite}
														title="Adresse & Localisation"
														icon="fa-location-dot"
														backMode="flip"
														backVisible={showMap}
														onBackToggle={toggleMapBack}
														backContent={
															<Map
																zoom={6}
																searchEnabled={false}
																locations={site ? [site] : []}
																resizeSignal={mapSignal}
															/>
														}
													>
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Adresse 1" value={[site?.Adresse1].filter(Boolean).join(" ")} />
															<InfoItem label="Adresse 2" value={[site?.Adresse2].filter(Boolean).join(" ")} />
															<InfoItem label="Adresse 3" value={[site?.Adresse3].filter(Boolean).join(" ")} />
															<InfoItem
																label="Code postal / Ville / Pays"
																value={[site?.CP, site?.Ville, site?.Pays || "France"].filter(Boolean).join(" · ")}
															/>

															<div className="sm:col-span-2">
																<div className="space-y-1">
																	<div className="text-gray-500 text-xs">Coordonnées GPS</div>
																	<button
																		type="button"
																		className="font-medium text-orange-600 text-sm hover:underline"
																		title="Basculer la carte"
																		onClick={() => toggleMapBack(!showMap)}
																	>
																		{`${site?.Localisation?.latitude}, ${site?.Localisation?.longitude}`}
																		<i className="ml-1 fa-solid fa-earth-europe" />
																	</button>
																</div>
															</div>

															<InfoItem label="Code chantier" value={site?.Code_Chantier} mono />
															<InfoItem label="Code compta" value={site?.Code_compta} mono />
														</div>
													</SectionCard>
												) : (
													<SectionCard
														loading={loadingSite}
														title="Adresse & Localisation"
														icon="fa-location-dot"
													>
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Adresse 1" value={[site?.Adresse1].filter(Boolean).join(" ")} />
															<InfoItem label="Adresse 2" value={[site?.Adresse2].filter(Boolean).join(" ")} />
															<InfoItem label="Adresse 3" value={[site?.Adresse3].filter(Boolean).join(" ")} />
															<InfoItem
																label="Code postal / Ville / Pays"
																value={[site?.CP, site?.Ville, site?.Pays || "France"].filter(Boolean).join(" · ")}
															/>

															<InfoItem label="Code chantier" value={site?.Code_Chantier} mono />
															<InfoItem label="Code compta" value={site?.Code_compta} mono />
														</div>
													</SectionCard>
												)}

											</div>

											{/* 3) Références opérationnelles */}
											{vis.refs && (
												<div className="col-span-1">
													<SectionCard loading={loadingSite} title="Références opérationnelles" icon="fa-hashtag">
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Opération" value={site?.Operation} />
															<InfoItem label="Numéro de marché" value={site?.Marche_Num} mono />
															<InfoItem label="Type de marché" value={site?.Type_Marche} />
															<InfoItem label="Lots" value={site?.Lot_Libelle} />
															<InfoItem label="Montant du marché" value={formatCurrency(site?.Total_Marche)} />
															<div className="gap-4 grid grid-cols-2 sm:col-span-2">
																<InfoItem label="Logements (neuf)" value={site?.Nb_Logements_Neuf ?? 0} />
																<InfoItem label="Logements (réhabilitation)" value={site?.Nb_Logements_Rea ?? 0} />
															</div>
														</div>
													</SectionCard>
												</div>
											)}

											{/* 4) Dates clés — peut être large si tu veux */}
											{vis.dates && (
												<div className="2xl:col-span-2">
													<SectionCard loading={loadingSite} title="Dates clés" icon="fa-calendar-days">
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
															<InfoItem label="Date DOC" value={formatDate(site?.Date_DOC)} />
															<InfoItem label="PV de réception" value={formatDate(site?.Date_PVRecep)} />
															<InfoItem label="Fin de garantie" value={formatDate(site?.Date_Fin_Garantie)} />
															<InfoItem label="GPA prise en charge" value={formatDate(site?.GPA_PriseEnCharge)} />
															<InfoItem label="Fin GPA" value={formatDate(site?.GPA_Fin)} />
															<InfoItem label="Fin GBF" value={formatDate(site?.GBF_Fin)} />
															<InfoItem label="GPA Visite" value={formatDate(site?.GPA_Visite)} />
															<InfoItem label="PV levée de réserves" value={formatDate(site?.PV_Levee_Reserves)} />
															<InfoItem label="Pré-transfert" value={formatDate(site?.Date_Prev_Trans)} />
															<InfoItem label="Transfert" value={formatDate(site?.Date_Transfert)} />
														</div>
													</SectionCard>
												</div>
											)}

											{/* 5) Archivage & Partage */}
											{vis.archive && (
												<div className="col-span-1">
													<SectionCard loading={loadingSite} title="Archivage & Partage" icon="fa-box-archive">
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-3">
															<InfoItem label="Classeur" value={site?.Archive_Classeur} />
															<InfoItem label="Situation" value={site?.Archive_Situation} />
															<InfoItem label="Salle" value={site?.Archive_Salle} />
															<InfoItem label="Rayonnage" value={site?.Archive_Rayonnage} />
															<InfoItem label="État du dossier" value={site?.Archive_Etat} />
														</div>
													</SectionCard>
												</div>
											)}

											{/* 6) Parties prenantes */}
											{vis.parties && (
												<div className="col-span-1">
													<SectionCard loading={loadingSite} title="Parties prenantes" icon="fa-people-group">
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Maître d’ouvrage" value={site?.Me_Ouvrage} />
															<InfoItem label="Maître d’œuvre" value={site?.Me_Oeuvre} />
															<InfoItem label="Propriétaire" value={site?.Proprietaire} />
														</div>
													</SectionCard>
												</div>
											)}

											{/* 7) Financier */}
											{vis.financier && (
												<div className="col-span-1">
													<SectionCard loading={loadingSite} title="Financier" icon="fa-scale-balanced">
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Coût des pathologies" value={formatCurrency(site?.Total_Pathologie)} />
															<InfoItem label="Coût des déclarations" value={formatCurrency(site?.Total_A_Payer)} />
														</div>
													</SectionCard>
												</div>
											)}

											{/* 8) D.O. */}
											{vis.do && (
												<div className="col-span-1">
													<SectionCard loading={loadingSite} title="Assurance D.O." icon="fa-shield-halved">
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Nom de l’assureur" value={site?.DO_Assureur} />
															<InfoItem label="Numéro de police D.O." value={site?.DO_Num_Police} mono />
														</div>
													</SectionCard>
												</div>
											)}

											{/* 9) Divers / Réserves */}
											{vis.divers && (
												<div className="col-span-1">
													<SectionCard loading={loadingSite} title="Divers / Réserves" icon="fa-file-lines">
														<div className="space-y-1">
															<div className="text-gray-500 text-xs">Réceptions / Réserves multiples</div>
															<div className="font-medium text-sm whitespace-pre-wrap">
																{sanitizeText(site?.Reception_Multiple) || <span className="text-gray-400">—</span>}
															</div>
														</div>
													</SectionCard>
												</div>
											)}

											{/* 10) Remarques */}
											{vis.remarques && (
												<div className="col-span-1">
													<SectionCard loading={loadingSite} title="Remarques" icon="fa-note-sticky">
														<div className="space-y-1">
															<div className="text-gray-500 text-xs">Remarques</div>
															<div className="font-medium text-sm whitespace-pre-wrap">
																{sanitizeText(site?.Remarques) === '' ? <span className="text-gray-400">—</span> : sanitizeText(site?.Remarques)}
															</div>
														</div>
													</SectionCard>
												</div>
											)}

											{/* 11) Liens externes */}
											{vis.liens && (
												<div className="lg:col-span-3 2xl:col-span-3">
													<ExternalLinksCard
														lstLiensExternes={liensExts}
														loading={loadingLiens}
														mutating={mutating}
														defaultUUIDExterne={uuid}
														defaultOrigine="Site"
														onBulkSave={(list) => saveLiensExternesDiff(uuid, "Site", list)}
													/>
												</div>
											)}
										</div>
									</div>
								)}
								{activeTab === 'declarations' && (
									<ListDeclaration lstDeclarations={siteDeclarations} ExclusivePage={"SiteDetail"} columns={columnsDeclarations} />
								)}
								{activeTab === 'lstIntervenants' && (
									<ListIntervenant ExclusivePage={false} lstIntervenants={relIntervenants} showAddButton={false} columns={columnsIntervenant} />
								)}
								{activeTab === 'docs' && (
									<ListDoxExt lstDoxExt={siteDoxExt} refreshDoxExt={refreshDoxExt} />
								)}
							</div>
						</div>

					</div>
				</div>
			</div>
		</div>

	);
};

export default SiteDetail;